import { Component, DestroyRef, inject, Input, OnChanges, OnInit, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged, forkJoin, lastValueFrom, Observable } from 'rxjs';

import { UserLoginService } from 'src/app/login/userlogin.service';
import { UserRoleService } from 'src/app/Common/UserRoleService/userRole.service';
import { UsersService } from 'src/app/master/users/users.service';
import { UserMasterSuggestUserNameRequestDTO } from 'src/app/master/users/users.interface';
import { formatDateForInput } from 'src/app/Shared Utilities/CustomFunctions/CustomFunctions';

// Module UAM services
import { HospitalUserAffiliationService } from 'src/app/hospital/HospitalUserMapping/user-affiliation-mapping.service';
import { UserAffiliationMappingService as SchoolUAMService } from 'src/app/School/SchoolUserMapping/user-affiliation-mapping.service';
import { UserAffiliationMappingService as SocietyUAMService } from 'src/app/housing-society/Interface & Service/userAffiliationMapping/userAffiliationMapping.service';

// Master data services for dropdowns
import { DepartmentMasterService } from 'src/app/hospital/BackOffice/hospital-master/hospital-detail/hospital-department/hospital-department.service';
import { BuildingService } from 'src/app/housing-society/Interface & Service/building/building.service';
import { FlatService } from 'src/app/housing-society/Interface & Service/flat/flat.service';

import { roleIds } from 'src/environment/environment';

export interface UnifiedOnboardingRequest {
  mappingId: number;
  userId: number;
  firstName: string;
  middleName: string;
  lastName: string;
  mobileNo: string;
  emailId: string;
  affiliationType: string;
  designation: string;
  status: string;
  submittedOn?: string | Date;
  gender?: string;
  dob?: string | Date;
  address?: string;
  pinCode?: string;
  // Hospital
  department?: string;
  specialization?: string;
  // Society
  buildingId?: number;
  buildingName?: string;
  flatId?: number;
  flatNumber?: string;
  // Raw payload
  raw: any;
}

@Component({
  selector: 'app-onboarding-requests',
  templateUrl: './onboarding-requests.component.html',
  styleUrls: ['./onboarding-requests.component.css']
})
export class OnboardingRequestsComponent implements OnInit, OnChanges {
  @Input() module: 'hospital' | 'school' | 'society' = 'hospital';
  @Input() moduleId: number = 0;
  @Input() moduleName: string = '';

  @ViewChild('previewModal') previewModal!: TemplateRef<any>;

  // Services
  private destroyRef              = inject(DestroyRef);
  private modalService            = inject(NgbModal);
  private toastr                  = inject(ToastrService);
  private userLoginService        = inject(UserLoginService);
  private userRoleService         = inject(UserRoleService);
  private usersService            = inject(UsersService);
  private hospitalUAMService      = inject(HospitalUserAffiliationService);
  private schoolUAMService        = inject(SchoolUAMService);
  private societyUAMService       = inject(SocietyUAMService);
  private departmentMasterService = inject(DepartmentMasterService);
  private buildingService         = inject(BuildingService);
  private flatService             = inject(FlatService);

  currentUser = this.userLoginService.User();

  // State
  isLoading: boolean = false;
  isSubmitting: boolean = false;
  allRequests: UnifiedOnboardingRequest[] = [];
  filteredRequests: UnifiedOnboardingRequest[] = [];

  // Dropdown reference data
  departments: any[] = [];
  buildings: any[]   = [];
  flats: any[]       = [];
  flatsLoading: boolean = false;

  // Filter & Search
  searchTerm: string = '';
  selectedAffiliationFilter: string = 'ALL';
  selectedStatusTab: 'Pending' | 'Approved' | 'All' = 'Pending';

  // Pagination
  currentPage: number = 1;
  pageSize: number = 10;
  pageSizeOptions: number[] = [5, 10, 20, 50];

  // Active item in review
  selectedRequest: UnifiedOnboardingRequest | null = null;
  modalRef!: NgbModalRef;
  /** Approved requests open read-only */
  isViewOnly: boolean = false;

  /** Flat to select once the flats of the request's building have loaded */
  private pendingFlatId: number = 0;
  private pendingFlatNumber: string = '';

  // Username validation state
  isCheckingUserName: boolean = false;
  userNameError: string = '';
  userNameValid: boolean | null = null;
  showPassword: boolean = false;

  // Review & Approve Form
  reviewForm = new FormGroup({
    firstName:       new FormControl('', Validators.required),
    middleName:      new FormControl(''),
    lastName:        new FormControl('', Validators.required),
    mobileNo:        new FormControl('', [Validators.required, Validators.pattern(/^[6-9]\d{9}$/)]),
    emailId:         new FormControl('', Validators.email),
    gender:          new FormControl(''),
    dob:             new FormControl(''),
    designation:     new FormControl(''),
    address:         new FormControl(''),
    pinCode:         new FormControl('', Validators.pattern(/^([1-9][0-9]{5})?$/)),
    // Hospital
    department:      new FormControl(''),
    specialization:  new FormControl(''),
    // Society
    buildingId:      new FormControl<number>(0),
    flatId:          new FormControl<number>(0),
    // Login credentials (the admin types both, nothing is suggested for the password)
    userName:        new FormControl('', [Validators.required]),
    userPassword:    new FormControl('', [Validators.required, Validators.minLength(6)])
  });

  // Dynamic affiliation filter options
  get affiliationOptions(): { label: string; value: string }[] {
    if (this.module === 'hospital') {
      return [
        { label: 'All Roles', value: 'ALL' },
        { label: 'Doctor', value: 'Doctor' },
        { label: 'Nurse / Receptionist', value: 'Nurse/Receptionist' },
        { label: 'Other Staff', value: 'OtherStaff' }
      ];
    }
    if (this.module === 'school') {
      // Student admissions no longer come through here: they are created in the
      // student admission table and approved from the admission list.
      return [
        { label: 'All Roles', value: 'ALL' },
        { label: 'Teaching Staff', value: 'TeachingStaff' },
        { label: 'Non-Teaching Staff', value: 'NonTeachingStaff' },
        { label: 'Driver', value: 'Drivers' }
      ];
    }
    return [
      { label: 'All Roles', value: 'ALL' },
      { label: 'Flat Member', value: 'FlatMember' },
      { label: 'Security Staff', value: 'SecurityStaff' },
      { label: 'General Staff', value: 'GeneralStaff' }
    ];
  }

  /**
   * Maps whatever the backend stores in affiliationType to one of the
   * dropdown option values, so filtering and labels are tolerant of spelling.
   */
  private roleKey(affiliationType: string): string {
    const a = (affiliationType || '').toLowerCase().replace(/[^a-z]/g, '');

    if (this.module === 'hospital') {
      if (a.includes('doctor')) return 'Doctor';
      if (a.includes('nurse') || a.includes('reception')) return 'Nurse/Receptionist';
      return 'OtherStaff';
    }

    if (this.module === 'school') {
      if (a.includes('non'))    return 'NonTeachingStaff';
      if (a.includes('driver')) return 'Drivers';
      if (a.includes('teach'))  return 'TeachingStaff';
      return '';
    }

    // Society
    if (a.includes('security')) return 'SecurityStaff';
    if (a.includes('flat') || a.includes('member') || a.includes('resident') ||
        a.includes('owner') || a.includes('tenant')) return 'FlatMember';
    return 'GeneralStaff';
  }

  /** Friendly role name for the table and the modal */
  roleLabel(affiliationType: string): string {
    const key = this.roleKey(affiliationType);
    const found = this.affiliationOptions.find(o => o.value === key);
    return found && found.value !== 'ALL' ? found.label : (affiliationType || '-');
  }

  /** System role id that is granted on approval. Decided from the request, never picked by hand. */
  get assignedRoleId(): number {
    return this.mapDefaultRoleId(this.selectedRequest?.affiliationType || '');
  }

  get pendingCount(): number {
    return this.allRequests.filter(r => r.status.toLowerCase() === 'pending').length;
  }

  ngOnInit(): void {
    if (this.moduleId) {
      this.loadRequests();
      this.loadReferenceDropdowns();
    }

    // Real-time username check with debounce
    this.reviewForm.controls.userName.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(val => {
        const userName = (val || '').trim();
        if (userName.length < 3) {
          this.userNameError = '';
          this.userNameValid = null;
          this.isCheckingUserName = false;
          return;
        }
        this.checkUserNameAvailability(userName);
      });

    // Society: building changed -> load its flats (reactive forms do not fire ngModelChange)
    this.reviewForm.controls.buildingId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(id => this.onBuildingSelected(Number(id) || 0));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['moduleId'] && !changes['moduleId'].firstChange && this.moduleId) {
      this.loadRequests();
      this.loadReferenceDropdowns();
    }
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // DATA LOADING
  // ══════════════════════════════════════════════════════════════════════════════
  loadRequests(): void {
    if (!this.moduleId) return;
    this.isLoading = true;

    const done = (items: any, mapper: (r: any) => UnifiedOnboardingRequest) => {
      const list = Array.isArray(items) ? items : [];
      this.allRequests = list.map(mapper).filter(r => this.isSelfOnboarded(r));
      this.applyFilters();
      this.isLoading = false;
    };

    if (this.module === 'hospital') {
      this.hospitalUAMService.HospitalUserAffiliationReadByHospitalId({ hospitalId: this.moduleId })
        .subscribe({
          next: (res: any) => done(res?.items || res?.data?.items || res?.data || res || [], r => this.mapHospitalRequest(r)),
          error: () => {
            this.isLoading = false;
            this.toastr.error('Failed to load hospital requests.');
          }
        });

    } else if (this.module === 'school') {
      this.schoolUAMService.UserAffiliationMapping_ReadUserBySchoolId({
        schoolId: this.moduleId,
        affiliationType: '',
        pageSize: 500,
        pageNo: 1
      }).subscribe({
        next: (res: any) => done(res?.items || res?.data?.items || res?.data || res || [], r => this.mapSchoolRequest(r)),
        error: () => {
          this.isLoading = false;
          this.toastr.error('Failed to load school requests.');
        }
      });

    } else if (this.module === 'society') {
      // The SP returns ONLY pending users when whereClause contains VerificationStatus='Pending',
      // and ONLY non-pending users otherwise. So we make two calls and tag each result.
      const base = { societyId: this.moduleId, affiliationType: '', pageSize: 500, pageNo: 1 };
      const extract = (res: any): any[] => {
        const items = res?.items || res?.data?.items || res?.data || res || [];
        return Array.isArray(items) ? items : [];
      };

      forkJoin({
        pending:  this.societyUAMService.SmUserAffiliationMapping_ReadUserBySocietyId(
                    { ...base, whereClause: "VerificationStatus='Pending'" } as any),
        approved: this.societyUAMService.SmUserAffiliationMapping_ReadUserBySocietyId(
                    { ...base, whereClause: '' } as any)
      }).subscribe({
        next: ({ pending, approved }: any) => {
          const pendingList  = extract(pending).map(r => this.mapSocietyRequest(r, 'Pending'));
          const approvedList = extract(approved).map(r => this.mapSocietyRequest(r, 'Approved'));

          this.allRequests = [...pendingList, ...approvedList].filter(r => this.isSelfOnboarded(r));
          this.applyFilters();
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.toastr.error('Failed to load society requests.');
        }
      });
    }
  }

  /** Only requests that came from the self-onboarding link (or are still waiting for approval) */
  private isSelfOnboarded(r: UnifiedOnboardingRequest): boolean {
    return r.status === 'Pending' || (r.raw?.otherDetail2 || '') === 'SELF_ONBOARDING';
  }

  // Reference lists for dropdowns inside the review modal
  loadReferenceDropdowns(): void {
    if (this.module === 'hospital') {
      this.departmentMasterService.DepartmentMaster_ReadByHospitalId({ hospitalId: this.moduleId })
        .subscribe({ next: (res: any) => { this.departments = res?.items || res?.data || []; } });

    } else if (this.module === 'society') {
      this.buildingService.Building_ReadBySocietyId({ societyId: this.moduleId })
        .subscribe({ next: (res: any) => { this.buildings = res?.items || res?.data || []; } });
    }
  }

  getBuildingName(buildingId?: number): string {
    const id = Number(buildingId) || 0;
    return this.buildings.find(b => Number(b.buildingId) === id)?.buildingName || '';
  }

  /**
   * Building changed (by the admin, or by openReviewModal patching the form).
   * Loads the flats and, when a flat was pre-selected for the request, selects it
   * only after the list has arrived.
   */
  private onBuildingSelected(buildingId: number): void {
    if (this.module !== 'society') return;

    const keepFlatId     = this.pendingFlatId;
    const keepFlatNumber = this.pendingFlatNumber;
    this.pendingFlatId     = 0;
    this.pendingFlatNumber = '';

    const flatCtrl = this.reviewForm.controls.flatId;
    this.flats = [];
    flatCtrl.setValue(0, { emitEvent: false });

    if (!buildingId) {
      if (!this.isViewOnly) flatCtrl.disable({ emitEvent: false });
      return;
    }
    if (!this.isViewOnly) flatCtrl.enable({ emitEvent: false });

    this.flatsLoading = true;
    this.flatService.Flat_ReadByBuildingId({ buildingId })
      .subscribe({
        next: (res: any) => {
          this.flats = res?.items || res?.data || [];
          this.flatsLoading = false;

          if (keepFlatId) {
            // The requested flat should always be selectable, even if the list misses it
            if (!this.flats.some(f => Number(f.flatId) === keepFlatId)) {
              this.flats = [...this.flats, { flatId: keepFlatId, flatNumber: keepFlatNumber || `Flat ${keepFlatId}` }];
            }
            flatCtrl.setValue(keepFlatId, { emitEvent: false });
          }
        },
        error: () => {
          this.flatsLoading = false;
        }
      });
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // MAPPERS
  // ══════════════════════════════════════════════════════════════════════════════
  private mapHospitalRequest(r: any): UnifiedOnboardingRequest {
    const isPending = (r.otherDetail1 || '').toLowerCase() === 'pending' || (r.verificationStatus || '').toLowerCase() === 'pending';
    return {
      mappingId:       r.mappingId || 0,
      userId:          r.userId || 0,
      firstName:       r.firstName || '',
      middleName:      r.middleName || '',
      lastName:        r.lastName || '',
      mobileNo:        r.mobileNo || '',
      emailId:         r.emailId || '',
      affiliationType: r.affiliationType || 'Doctor',
      designation:     r.designation || '',
      status:          isPending ? 'Pending' : 'Approved',
      submittedOn:     r.createdOn || r.joinedOn,
      gender:          r.gender || '',
      dob:             r.dob,
      address:         r.address || '',
      pinCode:         r.pinCode || '',
      department:      r.department || '',
      specialization:  r.specialization || '',
      raw:             r
    };
  }

  private mapSchoolRequest(r: any): UnifiedOnboardingRequest {
    const isPending = (r.otherDetail1 || '').toLowerCase() === 'pending' || (r.verificationStatus || '').toLowerCase() === 'pending';
    return {
      mappingId:       r.mappingId || 0,
      userId:          r.userId || 0,
      firstName:       r.firstName || '',
      middleName:      r.middleName || '',
      lastName:        r.lastName || '',
      mobileNo:        r.mobileNo || '',
      emailId:         r.emailId || '',
      affiliationType: r.affiliationType || 'TeachingStaff',
      designation:     r.designation || '',
      status:          isPending ? 'Pending' : 'Approved',
      submittedOn:     r.createdOn || r.joinedOn,
      gender:          r.gender || '',
      dob:             r.dob,
      address:         r.address || '',
      pinCode:         r.pinCode || r.pincode || '',
      raw:             r
    };
  }

  /** Status comes from which call returned the row: the SP does not return VerificationStatus */
  private mapSocietyRequest(r: any, status: 'Pending' | 'Approved'): UnifiedOnboardingRequest {
    return {
      mappingId:       r.mappingId || 0,
      userId:          r.userId || 0,
      firstName:       r.firstName || '',
      middleName:      r.middleName || '',
      lastName:        r.lastName || '',
      mobileNo:        r.mobileNo || '',
      emailId:         r.emailId || '',
      affiliationType: r.affiliationType || 'FlatMember',
      designation:     r.designation || '',
      status:          status,
      submittedOn:     r.createdOn || r.joinedOn,
      gender:          r.gender || '',
      dob:             r.dob,
      address:         r.address || '',
      pinCode:         r.pinCode || '',
      buildingId:      Number(r.buildingId) || 0,
      buildingName:    r.buildingName || '',
      flatId:          Number(r.flatId) || 0,
      flatNumber:      r.flatNumber || r.flatNo || '',
      raw:             r
    };
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // FILTERING & PAGINATION
  // ══════════════════════════════════════════════════════════════════════════════
  applyFilters(): void {
    let list = [...this.allRequests];

    if (this.selectedStatusTab !== 'All') {
      list = list.filter(r => r.status.toLowerCase() === this.selectedStatusTab.toLowerCase());
    }

    if (this.selectedAffiliationFilter !== 'ALL') {
      list = list.filter(r => this.roleKey(r.affiliationType) === this.selectedAffiliationFilter);
    }

    const q = this.searchTerm.trim().toLowerCase();
    if (q) {
      list = list.filter(r =>
        `${r.firstName} ${r.middleName} ${r.lastName}`.toLowerCase().includes(q) ||
        (r.mobileNo || '').includes(q) ||
        (r.emailId || '').toLowerCase().includes(q) ||
        (r.affiliationType || '').toLowerCase().includes(q) ||
        this.roleLabel(r.affiliationType).toLowerCase().includes(q) ||
        (r.designation || '').toLowerCase().includes(q) ||
        (r.department || '').toLowerCase().includes(q)
      );
    }

    this.filteredRequests = list;
    this.currentPage = 1;
  }

  onSearchChange(): void { this.applyFilters(); }
  onAffiliationFilterChange(): void { this.applyFilters(); }

  switchStatusTab(tab: 'Pending' | 'Approved' | 'All'): void {
    this.selectedStatusTab = tab;
    this.applyFilters();
  }

  get paginatedRequests(): UnifiedOnboardingRequest[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredRequests.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredRequests.length / this.pageSize) || 1;
  }

  get pageNumbers(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  get startRecordIndex(): number {
    if (!this.filteredRequests.length) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endRecordIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.filteredRequests.length);
  }

  goToPage(p: number): void {
    if (p >= 1 && p <= this.totalPages) this.currentPage = p;
  }

  onPageSizeChange(size: number): void {
    this.pageSize = Number(size);
    this.currentPage = 1;
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // REVIEW & APPROVE MODAL
  // ══════════════════════════════════════════════════════════════════════════════
  openReviewModal(request: UnifiedOnboardingRequest): void {
    this.selectedRequest = request;
    this.isViewOnly      = request.status.toLowerCase() !== 'pending';
    this.userNameError   = '';
    this.userNameValid   = null;
    this.isCheckingUserName = false;
    this.showPassword    = false;
    this.flats           = [];

    // Remember the flat; it is selected as soon as the building's flats have loaded
    this.pendingFlatId     = this.module === 'society' ? (Number(request.flatId) || 0) : 0;
    this.pendingFlatNumber = request.flatNumber || '';

    this.reviewForm.enable({ emitEvent: false });
    this.reviewForm.reset({}, { emitEvent: false });

    this.reviewForm.patchValue({
      firstName:      request.firstName,
      middleName:     request.middleName,
      lastName:       request.lastName,
      mobileNo:       request.mobileNo,
      emailId:        request.emailId,
      gender:         request.gender || '',
      dob:            this.toDateInput(request.dob),
      designation:    request.designation || this.roleLabel(request.affiliationType),
      address:        request.address || '',
      pinCode:        request.pinCode || '',
      department:     request.department || '',
      specialization: request.specialization || '',
      flatId:         Number(request.flatId) || 0,
      userName:       '',
      userPassword:   ''
    });

    // buildingId goes in last: its valueChanges loads the flats and applies pendingFlatId
    this.reviewForm.controls.buildingId.setValue(Number(request.buildingId) || 0);

    if (this.isViewOnly) {
      this.reviewForm.disable({ emitEvent: false });
    } else {
      this.suggestUserName(request);
    }

    this.modalRef = this.modalService.open(this.previewModal, {
      size: 'lg',
      backdrop: 'static',
      keyboard: false
    });
  }

  private toDateInput(value: any): string {
    if (!value) return '';
    try { return formatDateForInput(value) || ''; } catch { return ''; }
  }

  /** Only the username is suggested. The password is always typed by the admin. */
  private suggestUserName(request: UnifiedOnboardingRequest): void {
    const fallback = () => {
      const first = (request.firstName || 'user').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
      const phone = (request.mobileNo || '').slice(-4);
      this.reviewForm.controls.userName.setValue(`${first}${phone}`);
    };

    const payload: UserMasterSuggestUserNameRequestDTO = {
      firstName: request.firstName || '',
      lastName:  request.lastName || ''
    };

    this.usersService.UserMaster_SuggestUserName(payload).subscribe({
      next: (res: any) => {
        if (res?.suggestedUserName) this.reviewForm.controls.userName.setValue(res.suggestedUserName);
        else fallback();
      },
      error: () => fallback()
    });
  }

  /** Role granted on approval, based only on what the person applied as */
  private mapDefaultRoleId(affiliation: string): number {
    const aff = (affiliation || '').toLowerCase();

    if (this.module === 'hospital') {
      if (aff.includes('doctor'))    return roleIds.hospitalDoctor;
      if (aff.includes('nurse'))     return roleIds.hospitalNurse;
      if (aff.includes('reception')) return roleIds.hospitalReceptionist;
      return roleIds.hospitalOtherStaff;
    }

    if (this.module === 'school') {
      if (aff.includes('non'))       return roleIds.nonTeachingStaff;
      if (aff.includes('driver'))    return roleIds.driver;
      if (aff.includes('admission') || aff.includes('student')) return roleIds.student;
      return roleIds.teacher;
    }

    // Society
    if (aff.includes('flat') || aff.includes('member') || aff.includes('resident')) return roleIds.societyFlatMember;
    if (aff.includes('security')) return roleIds.societySecurityStaff;
    return 20; // General staff
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  checkUserNameAvailability(userName: string): void {
    this.isCheckingUserName = true;
    this.userNameValid = null;
    this.userNameError = '';

    this.userLoginService.UserLogin_ValidateUserName({ userName }).subscribe({
      next: (res: any) => {
        this.isCheckingUserName = false;
        const ctrl = this.reviewForm.controls.userName;

        if (res?.responseCode === 409) {
          this.userNameValid = false;
          this.userNameError = res.responseMsg || 'Username already exists';
          ctrl.setErrors({ ...(ctrl.errors || {}), duplicate: true });
        } else {
          this.userNameValid = true;
          const errors = ctrl.errors;
          if (errors) {
            delete errors['duplicate'];
            ctrl.setErrors(Object.keys(errors).length ? errors : null);
          }
        }
      },
      error: () => {
        this.isCheckingUserName = false;
        this.userNameValid = false;
        this.userNameError = 'Error checking username availability.';
      }
    });
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // APPROVAL SUBMISSION  (user update -> role -> login -> affiliation approved)
  // ══════════════════════════════════════════════════════════════════════════════
  async submitApproval(): Promise<void> {
    if (this.isSubmitting || this.isViewOnly || !this.selectedRequest) return;

    this.reviewForm.markAllAsTouched();
    if (this.reviewForm.invalid) {
      this.toastr.warning('Please complete all required fields correctly.');
      return;
    }
    if (this.userNameValid === false) {
      this.toastr.error('Please enter a valid username.');
      return;
    }

    const v: any        = this.reviewForm.getRawValue();
    const req           = this.selectedRequest;
    const userId        = req.userId;
    const actionUser    = String(this.currentUser?.userId || 'ADMIN');
    const roleId        = this.assignedRoleId;

    this.isSubmitting = true;

    try {
      // 1. Keep the user master in sync with what the admin corrected
      const userUpdate = {
        userId,
        firstName:    (v.firstName || '').trim(),
        middleName:   (v.middleName || '').trim(),
        lastName:     (v.lastName || '').trim(),
        dob:          v.dob ? new Date(v.dob) : new Date('2000-01-01'),
        mobileNo:     v.mobileNo || '',
        emailId:      v.emailId || '',
        designation:  v.designation || '',
        profileImage: '',
        gender:       v.gender || '',
        address:      v.address || '',
        pinCode:      v.pinCode || '',
        isActive:     1,
        actionUser
      };
      await lastValueFrom(this.usersService.UserMaster_Update(userUpdate));

      // 2. Role (automatic, based on the requested affiliation). It may already exist, which is fine.
      try {
        await lastValueFrom(this.userRoleService.UserRoleService_Create({ userId, roleId, actionUser }));
      } catch { /* role already assigned */ }

      // 3. Login with the username and password the admin typed
      try {
        await lastValueFrom(this.userLoginService.UserLogin_Create({
          userId,
          userName:     (v.userName || '').trim().toLowerCase(),
          userPassword: v.userPassword,
          status:       'Expired',      // same as the member screen: the user sets their own password at first login
          actionUser
        } as any));
      } catch (loginErr: any) {
        const alreadyExists = loginErr?.status === 409 || loginErr?.error?.responseCode === 409;
        if (!alreadyExists) throw loginErr;
      }

      // 4. Mark the affiliation as approved
      await lastValueFrom(this.buildAffiliationUpdate(v, actionUser, roleId));

      this.toastr.success('User approved and login credentials granted successfully!');
      this.modalRef?.close();
      this.loadRequests();
    } catch (err: any) {
      this.toastr.error(err?.error?.message || 'Failed to approve the request.');
    } finally {
      this.isSubmitting = false;
    }
  }

  private buildAffiliationUpdate(v: any, actionUser: string, roleId: number): Observable<any> {
    const req = this.selectedRequest!;
    const common = {
      ...req.raw,
      mappingId:   req.mappingId,
      userId:      req.userId,
      firstName:   (v.firstName || '').trim(),
      middleName:  (v.middleName || '').trim(),
      lastName:    (v.lastName || '').trim(),
      mobileNo:    v.mobileNo,
      emailId:     v.emailId,
      designation: v.designation,
      address:     v.address,
      pinCode:     v.pinCode,
      gender:      v.gender,
      otherDetail1: 'Approved',
      actionUser
    };

    if (this.module === 'hospital') {
      return this.hospitalUAMService.HospitalUserAffiliationUpdate({
        ...common,
        hospitalId:         this.moduleId,
        department:         v.department,
        specialization:     v.specialization,
        verificationStatus: 'Approved'
      });
    }

    if (this.module === 'school') {
      return this.schoolUAMService.UserAffiliationMapping_Update({
        ...common,
        schoolId:       this.moduleId,
        assignedRoleId: roleId
      });
    }

    return this.societyUAMService.SmUserAffiliationMapping_Update({
      ...common,
      societyId:          this.moduleId,
      buildingId:         Number(v.buildingId) || req.raw?.buildingId || 0,
      flatId:             Number(v.flatId) || req.raw?.flatId || undefined,
      isPrimary:          (v.designation || '').toLowerCase() === 'owner' ? 1 : 0,
      verificationStatus: 'Approved'
    });
  }

  closeModal(): void {
    this.modalRef?.close();
  }
}